<template>
  <div
    class="workflow-container h-100"
    :style="showSidePanel ? `margin-right: ${sidePanelTab === 'chatLog' ? 518 : 398}px;` : ''"
  >
    <v-alert
      v-if="isAdminWithoutMembership && !loadingState && !globalLoadingState && !loadingActiveSubscription"
      type="error"
      border="start"
      class="admin-warning"
    >
      Warning, you are logged in as <strong>Admin!</strong> Don't update customer workflow unless permitted.
    </v-alert>
    <div v-if="!loadingState && !loadingActiveSubscription && workflow && workflow.id === $route.params.id"
         class="d-flex flex-column h-100"
         :data-test-workflow="true"
         :data-test-version="latestVersionNumberForCypress"
    >
      <navigation-top v-if="!isEmbedMode">
        <navigation-top-workflow
          :previousVersionExists="previousVersionExists"
          :versions="versions"
          :releases="releases"
          :showDone="showDone"
        />
      </navigation-top>

      <bpmn
        ref="bpmn"
        :key="JSON.stringify(showReleaseNo)"
        :workflow="workflow"
        :show-done="showDone"
        @move-right="moveRight"
        @move-left="moveLeft"
        @on-copy="onCopy"
        @on-paste="onPaste"
        @on-cut="onCut"
        @update-selected-event-ids="selectedEventIds = $event"
      ></bpmn>

      <div v-if="!isEmbedMode" class="bottom-container">
        <div class="tab-button-container">
          <v-tabs
            :modelValue="selectedTab"
            @update:model-value="openTab($event)"
            class="new-tab large"
          >
            <v-tab>Domain Model</v-tab>
            <v-tab>Entities</v-tab>
            <v-tab>Read Models</v-tab>
            <v-tab>Domain Events</v-tab>
            <v-tab>Backlog</v-tab>
            <v-tab>User Story Map</v-tab>
            <v-tab>History</v-tab>
          </v-tabs>
        </div>

        <workflow-backlog
          v-if="Number(selectedTab) === 4 || Number(selectedTab) === 5"
          :eventId="null"
          :workflow="workflow"
          :show-done="showDone"
          :jiraData2="jiraData2"
          :loadingJiraInfo="loadingJiraInfo"
          :selectedTab="selectedTab"
          :releases="releases"
          @show-all="showAll"
          @display-done="onShowDoneChange"
          @ask-for-reply="onAskForReply"
          ref="backlog"
        ></workflow-backlog>

        <domain-model v-if="Number(selectedTab) === 0 && svgGraphData && svgLanesData" />

        <entities v-if="Number(selectedTab) === 1" />

        <read-models v-if="Number(selectedTab) === 2" />

        <domain-events v-if="Number(selectedTab) === 3" />

        <workflow-history
          :workflow="workflow"
          :versions="versions"
          v-if="Number(selectedTab) === 6"
        ></workflow-history>

        <workflow-delete-item-dialog
          ref="deleteItemDialog"
          :itemType="'Event'"
          :hideIcon="true"
          :selectedEventIds="selectedEventIds"
          :isMultiSelection="selectedEventIds.length > 1"
          @delete-multiple-events="deleteMultipleEvents"
        ></workflow-delete-item-dialog>

        <workflow-description-required-dialog
          :model-value="workflowDescriptionDialogOpen"
          @update:model-value="setWorkflowDescriptionDialogOpen"
        />

        <retry-generation-dialog />
      </div>
    </div>
    <not-found v-if='!globalLoadingState && !loadingState && !workflow && (!isUserLoggedIn || projectMemberships !== null)' />
  </div>
</template>

<script>
import Bpmn from '@/components/Bpmn'
import WorkflowBacklog from '@/components/WorkflowBacklog'
import Entities from '@/components/Entities.vue'
import ReadModels from '@/components/ReadModels.vue'
import DomainEvents from '@/components/DomainEvents.vue'
import WorkflowHistory from '@/components/WorkflowHistory'
import WorkflowDeleteItemDialog from '@/components/WorkflowDeleteItemDialog'
import workflowApi from '@/api/workflowApi'
import recentWorkflowsApi from '@/api/recentWorkflowsApi'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import { workflowHelpers } from '@/mixins/workflowHelpers.js'
import { scrollToEvent } from '@/utils/scroll'
import dataHelpers from '@/helpers/dataHelpers'
import cognitoAuth from '@/cognito'
import { EventBus } from '@/event-bus/event-bus'
import NavigationTop from '@/components/NavigationTop'
import DomainModel from '@/components/DomainModel.vue'
import NavigationTopWorkflow from '@/components/NavigationTopWorkflow'
import NotFound from '@/pages/NotFound'
import RetryGenerationDialog from '@/components/RetryGenerationDialog.vue'
import WorkflowDescriptionRequiredDialog from '@/components/WorkflowDescriptionRequiredDialog.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import 'svg2pdf.js'
import copyPasteHelpers from '../helpers/copyPasteHelpers'
import store from '@/store'
import randomUUID from '@/utils/uuid'
import safeStructuredClone from '@/utils/clone'
import { SchemaType } from '@/shared/constants/schema'
import { isEmbedMode as isEmbedRoute } from '@/utils/embed'
import { syncHelpHeroWorkflow } from '@/utils/helpHero'
import { runLinkAction, withoutLinkAction } from '@/utils/linkActions'

export default {
  name: 'Workflow',
  components: {
    Entities,
    ReadModels,
    DomainEvents,
    Bpmn,
    WorkflowBacklog,
    WorkflowHistory,
    NavigationTop,
    NotFound,
    WorkflowDeleteItemDialog,
    NavigationTopWorkflow,
    DomainModel,
    RetryGenerationDialog,
    WorkflowDescriptionRequiredDialog
  },
  mixins: [diagramHelpers, workflowHelpers],
  data () {
    return {
      eventType: '',
      showDone: false,
      selectedEventIds: [],
      latestVersionNumberForCypress: null,
      accessToken: null,
      loadingJiraInfo: 0,
      checkVersionTimeout: null
    }
  },
  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow,
      workflowHistory: state => state.workflowHistory.workflowHistory,
      jiraIssues: state => state.jira.jiraIssues,
      showReleaseNo: state => state.workflow.showReleaseNo,
      cardTypes: state => state.cardTypes.cardTypes,
      showSidePanel: state => state.showSidePanel,
      sidePanelTab: state => state.sidePanelTab,
      projectMemberships: state => state.projectMemberships.projectMemberships,
      isUserLoggedIn: state => state.isUserLoggedIn,
      adminUser: state => state.adminUser,
      teamMembers: state => state.teamMembers.teamMembers,
      userId: state => state.userId,
      workflowDescriptionDialogOpen: state => state.workflow.workflowDescriptionDialogOpen
    }),
    ...mapGetters({
      loadingState: 'workflow/getLoadingState',
      globalLoadingState: 'loading/getLoadingState',
      loadingActiveSubscription: 'loading/getLoadingActiveSubscription',
      svgGraphData: 'svgGraphData/svgGraphData',
      svgLanesData: 'svgGraphData/svgLanesData',
      svgGroupsData: 'svgGraphData/svgGroupsData'
    }),
    isViewingHistoricalVersion () {
      return this.$route.params.version !== undefined
    },
    isEmbedMode () {
      return isEmbedRoute(this.$route)
    },
    versions () {
      return this.workflowHistory ? [...this.workflowHistory] : []
    },
    pendingLinkAction () {
      const workflowReady = !this.loadingState && !this.loadingActiveSubscription && !this.isEmbedMode &&
        this.workflow?.id === this.$route.params.id
      return workflowReady ? this.$route.query?.action || null : null
    },
    selectedTab () {
      if (this.$route.query?.selectedTab) {
        return this.$route.query.selectedTab
      } else {
        return 0
      }
    },
    previousVersionExists () {
      let index
      if (this.workflow) {
        const restoreVersion = this.workflow.sameAsVersion ? (this.workflow.sameAsVersion - 1) : this.workflow.version - 1
        index = this.versions?.findIndex(element => element.version === restoreVersion)
      }
      return index >= 0
    },
    events () {
      return this.workflow?.domainEvents || []
    },
    releases () {
      if (!this.events) {
        return []
      }
      let cards = []
      for (const event of this.events) {
        if (event.cards) {
          cards = cards.concat(event.cards)
        }
      }
      const cardReleaseNumbers = new Set()
      for (const card of cards) {
        if (card.releaseNo) {
          cardReleaseNumbers.add(card.releaseNo)
        }
      }
      return [...cardReleaseNumbers]
        .sort(function (a, b) {
          return a - b
        })
        .map(value => {
          const release = this.workflow?.releases?.find(r => r.value === value)
          const customName = release?.name
          const displayName = customName || `R${value}`
          return {
            value,
            text: `R${value}`,
            title: displayName
          }
        })
    },
    selectedEvent () {
      const { eventId } = this.$route.params
      if (eventId && this.events) {
        const event = this.events.find(wt => wt.id === eventId)
        return event || null
      }
      return null
    },
    jiraData2 () {
      const result = this.jiraIssues?.reduce(function (map, req) {
        map[req.requirementId] = req
        return map
      }, {})
      return result || {}
    },
    isAdminWithoutMembership () {
      return this.adminUser && this.teamMembers?.findIndex(tm => tm.userId === this.userId) === -1
    }
  },
  watch: {
    projectMemberships: {
      handler: function (memberships, previousMemberships) {
        const clearedByLogout = !memberships && previousMemberships
        if (clearedByLogout) {
          return
        }
        if (!this.isUserLoggedIn || memberships) {
          this.updateWorkflowQueries(this.$route.params.id)
        }
      },
      immediate: true
    },
    'workflow.version': {
      handler: function (newVal) {
        this.latestVersionNumberForCypress = newVal
      },
      immediate: true
    },
    'workflow.status': {
      handler: function () {
        this.$store.commit('syncWorkflowDisabled')
      },
      immediate: true
    },
    'workflow.id': {
      handler: async function (newVal) {
        if (!this.$route.params.projectId || !this.$route.params.id || !newVal) {
          return
        }

        try {
          const session = await cognitoAuth.getSession()
          this.accessToken = session.getAccessToken().getJwtToken()
          if (this.accessToken) {
            this.$store.dispatch('jira/getJiraIssues', {
              apiToken: this.accessToken,
              projectId: this.$route.params.projectId,
              workflowId: this.$route.params.id
            })
          }
        } catch (error) {
        }
        recentWorkflowsApi.add(this.workflow)
      },
      immediate: true
    },
    pendingLinkAction: {
      handler: function (action) {
        if (!action) {
          return
        }
        // after render: side panel and settings reset their state on mount
        this.$nextTick(() => {
          runLinkAction(this.$store, this.$route.query)
          this.$router.replace({ query: withoutLinkAction(this.$route.query) })
        })
      },
      immediate: true
    },
    '$store.state.workflow.isViewOnlyEnabled': {
      handler: function () {
        this.$store.commit('syncWorkflowDisabled')
      }
    },
    teamMembers () {
      if (this.adminUser) {
        this.$store.commit('syncWorkflowDisabled')
      }
    }
  },
  created () {
    window.addEventListener('keydown', this.keydown)
    document.addEventListener('copy', this.onCopy)
    document.addEventListener('paste', (event) => {
      this.debounceUpdateWorkflow({ fn: this.onPaste, args: [event] })
    })
    document.addEventListener('cut', (event) => {
      this.debounceUpdateWorkflow({ fn: this.onCut, args: [event] })
    })
  },
  beforeUnmount () {
    // it is important to remove event listener when component is destroyed
    // otherwise component's reference in callback becomes broken
    window.removeEventListener('keydown', this.keydown)
    EventBus.$off('get-workflow')
    EventBus.$off('open-tab-event')
  },
  methods: {
    ...mapActions({
      debounceUpdateWorkflow: 'debounceUpdateWorkflow/debounceUpdateWorkflow'
    }),
    getSchemasUsedByEvents (events = []) {
      const schemaIds = new Set()
      for (const ev of events) {
        for (const card of ev?.cards || []) {
          if (card?.schemaId) {
            schemaIds.add(card.schemaId)
          }
        }
      }
      if (schemaIds.size === 0) {
        return []
      }
      return (this.workflow?.schemas || []).filter((s) => schemaIds.has(s.id))
    },
    /**
     * Returns { updatedSchemas, oldToNewSchemaId }
     * - updatedSchemas is the full workflow.schemas array to persist (or null if unchanged)
     * - oldToNewSchemaId maps clipboard schema IDs to target workflow schema IDs
     */
    prepareSchemasForPaste (clipboardSchemas = []) {
      const incoming = Array.isArray(clipboardSchemas) ? clipboardSchemas.filter(Boolean) : []
      const existing = this.workflow?.schemas || []

      const existingById = new Map(existing.map((s) => [s.id, s]))
      const existingByKey = new Map(
        existing.map((s) => [`${s.type}::${(s.name || '').trim().toLowerCase()}`, s])
      )

      const oldToNewSchemaId = new Map()
      const schemasToAdd = []

      for (const schema of incoming) {
        if (!schema?.id || !schema?.type || !schema?.name) {
          continue
        }
        if (existingById.has(schema.id)) {
          oldToNewSchemaId.set(schema.id, schema.id)
          continue
        }
        const key = `${schema.type}::${(schema.name || '').trim().toLowerCase()}`
        const match = existingByKey.get(key)
        if (match) {
          oldToNewSchemaId.set(schema.id, match.id)
          continue
        }
        const newId = randomUUID()
        oldToNewSchemaId.set(schema.id, newId)
        schemasToAdd.push({ ...schema, id: newId })
      }

      if (schemasToAdd.length === 0) {
        return { updatedSchemas: null, oldToNewSchemaId }
      }

      // Remap internal schema references for any schemas we are adding
      const remappedSchemasToAdd = schemasToAdd.map((schema) => {
        const entityId = schema.entityId && oldToNewSchemaId.get(schema.entityId)
        const fields = (schema.fields || []).map((field) => {
          const relatedEntityId = field.relatedEntityId && oldToNewSchemaId.get(field.relatedEntityId)
          return {
            ...field,
            relatedEntityId: relatedEntityId || field.relatedEntityId || undefined
          }
        })
        return {
          ...schema,
          entityId: entityId || schema.entityId || undefined,
          fields
        }
      })

      return { updatedSchemas: [...existing, ...remappedSchemasToAdd], oldToNewSchemaId }
    },
    setWorkflowDescriptionDialogOpen (value) {
      this.$store.commit('workflow/setWorkflowDescriptionDialogOpen', value)
    },
    openTab (tab) {
      this.$router.push({ query: { selectedTab: tab } })
    },
    onAskForReply (event) {
      this.$refs.bpmn.onAskForReply(event)
    },
    createMainParentChildEventMap (events) {
      return dataHelpers.createMainParentChildEventMap(events)
    },
    keydown (event) {
      if (this.documentHasDialogOverlay()) {
        // stop propagation if overlay is present
        // for example when help dialog is open
        // don't allow keyboard shortcuts in the diagram
        // because the user might not see the result
        event.stopPropagation()
        return
      }
      if (['INPUT', 'TEXTAREA'].includes(event.target.tagName) || event.target.contentEditable === 'true') {
        return
      }
      if (this.workflowSettingsOpen || this.workflowStatusCompleted || this.$store.state.workflow.isViewOnlyEnabled) {
        return
      }
      if (event.key === 'z' && (event.ctrlKey || event.metaKey) && !event.shiftKey) {
        this.debounceUpdateWorkflow({
          fn: workflowApi.undo,
          args: [this.workflow]
        })
        event.preventDefault()
      }
      if ((event.key === 'y' && event.ctrlKey) || (event.key === 'y' && event.metaKey) || (event.key === 'z' && event.metaKey && event.shiftKey)) {
        this.debounceUpdateWorkflow({
          fn: workflowApi.redo,
          args: [this.workflow]
        })
        event.preventDefault()
      }
      // Tool switching shortcuts
      if (event.key === 'h' && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        this.$store.commit('setInteractionMode', 'hand')
        event.preventDefault()
        return
      }
      if (event.key === 'v' && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        if (!this.workflowStatusCompleted && !this.$store.state.workflow.isViewOnlyEnabled) {
          this.$store.commit('setInteractionMode', 'pointer')
        }
        event.preventDefault()
        return
      }
      if (event.key === 'Backspace' && event.altKey === true) {
        // Check for multi-selection first
        const selectedEventIds = this.$refs.bpmn?.selectedEventIds || []
        const hasMultiSelection = Array.isArray(selectedEventIds) && selectedEventIds.length > 1

        // Delete single & multiple event
        if (this.selectedEvent || hasMultiSelection) {
          this.$refs.deleteItemDialog.openDialog()
        }
        return
      }
      if (!this.selectedEvent) {
        return
      }
      if (this.commentsDialog || this.dialog || this.exportDialog || this.exportJiraDialog) {
        return
      }
      if ((event.keyCode === 37 && event.altKey === false) || (event.keyCode === 9 && event.shiftKey === true)) { // left arrow (select previous event)
        this.moveLeft()
        event.preventDefault()
        return
      }
      if (event.keyCode === 38 && event.altKey === false) { // up arrow (select event above)
        const nodes = Object.values(this.svgGraphData).filter(node => node.x === this.svgGraphData[this.selectedEvent.id].x && node.y < this.svgGraphData[this.selectedEvent.id].y)
        nodes.sort(function (a, b) {
          return b.y - a.y
        })
        if (nodes[0]) {
          this.selectEvent(nodes[0].id)
          event.preventDefault()
        }
        return
      }
      if ((event.keyCode === 39 && event.altKey === false) || (event.keyCode === 9 && event.shiftKey === false)) { // right arrow (select next event)
        this.moveRight()
        event.preventDefault()
        return
      }
      if (event.keyCode === 40 && event.altKey === false) { // down arrow (select event below)
        const nodes = Object.values(this.svgGraphData).filter(node => (node.x === this.svgGraphData[this.selectedEvent.id].x) && (node.y > this.svgGraphData[this.selectedEvent.id].y))
        nodes.sort(function (a, b) {
          return a.y - b.y
        })
        if (nodes[0]) {
          this.selectEvent(nodes[0].id)
          event.preventDefault()
        }
        return
      }
      if (event.keyCode === 38 && event.altKey === true) { // option - up arrow (create event on lane above)
        if (this.workflowStatusCompleted) {
          return
        }

        const laneId = this.$refs.bpmn.svgLanes[this.selectedEvent.laneId].laneAbove
        if (laneId) {
          this.debounceUpdateWorkflow({
            fn: this.$refs.bpmn.addShape,
            args: [{ laneId }]
          })
        }
        return
      }
      if (event.keyCode === 39 && event.altKey === true) { // option - right arrow (create event on same lane)
        if (this.workflowStatusCompleted) {
          return
        }
        this.debounceUpdateWorkflow({ fn: this.$refs.bpmn.addShape, args: [] })
        return
      }
      if (event.keyCode === 40 && event.altKey === true) { // option - down arrow (create event on  lane below)
        if (this.workflowStatusCompleted) {
          return
        }
        event.preventDefault()
        const shape = { laneId: this.$refs.bpmn.svgLanes[this.selectedEvent.laneId].laneBelow }
        const options = { createNewLaneIfNoLaneId: true }
        this.debounceUpdateWorkflow({
          fn: this.$refs.bpmn.addShape,
          args: [shape, options]
        })
      }
    },
    moveLeft () {
      if (this.selectedEvent.parents[0] && this.selectedEvent.parents[0] !== 'start') {
        this.selectEvent(this.selectedEvent.parents[0])
      }
    },
    moveRight () {
      const nodes = Object.values(this.svgGraphData).filter(node => node.parents[0] === this.selectedEvent.id)
      const currentY = this.svgGraphData[this.selectedEvent.id].y
      nodes.sort((a, b) => {
        return Math.abs(currentY - a.y) - Math.abs(currentY - b.y)
      })
      if (nodes[0]) {
        this.selectEvent(nodes[0].id)
      }
    },
    showAll () {
      this.eventId = null
      this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.$route.params.id }).catch(() => {
      })
    },
    selectEvent (eventId) {
      this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.$route.params.id + '/' + eventId }).catch(() => {
      })
      scrollToEvent(eventId)
    },
    onShowDoneChange (val) {
      this.showDone = val
    },
    checkForVersionGap () {
      if (this.workflow) {
        const restoreVersion = this.workflow.sameAsVersion ? (this.workflow.sameAsVersion - 1) : this.workflow.version - 1
        const index = this.versions?.findIndex(element => element.version === restoreVersion)
        if (index === -1 && restoreVersion !== 0) {
          this.$store.dispatch('workflowHistory/listWorkflowHistory', {
            projectId: this.workflow.projectId,
            workflowId: this.workflow.id,
            subscribe: false
          })
        }
      }
      this.checkVersionTimeout = setTimeout(this.checkForVersionGap, 5000)
    },

    // copy active event to clipboard
    async onCopy (event, skipClearSelection = false, isCutOperation = false) {
      const selection = window.getSelection()
      const hasTextSelection = selection && selection.toString().trim().length > 0

      // Check for multi-selection
      const selectedEventIds = this.$refs.bpmn?.selectedEventIds || []
      const hasMultiSelection = Array.isArray(selectedEventIds) && selectedEventIds.length > 1

      // If multi-selection is active, always use custom copy logic
      if (!hasMultiSelection && event && (document.activeElement !== document.body || this.documentHasDialogOverlay() || hasTextSelection)) {
        // don't catch these events because they can be input fields or user-selected text (from sidebar, etc.)
        // if the overlay is present, don't copy with this function, run default copy
        return
      }

      if (hasMultiSelection) {
        // Copy multiple events
        const eventsToCopy = selectedEventIds
          .map(eventId => this.events.find(e => e.id === eventId))
          .filter(Boolean)

        if (eventsToCopy.length > 0) {
          const schemas = this.getSchemasUsedByEvents(eventsToCopy)
          const eventsData = eventsToCopy.map(event => ({
            ...event,
            workflowId: this.workflow.id,
            cardTypes: this.cardTypes,
            color: event.color ? event.color : this.workflow.eventDefinition.color,
            laneName: this.svgLanesData[event.laneId]?.name,
            parents: this.svgGraphData[event.id]?.parents || [],
            childIds: this.svgGraphData[event.id]?.childIds || [],
            offset: this.svgGraphData[event.id]?.offset
          }))

          const jsonData = JSON.stringify({
            isMultiSelection: true,
            isCutOperation,
            events: eventsData,
            schemas
          })

          const divElement = document.createElement('div')
          divElement.setAttribute('data-events-json', jsonData)
          divElement.id = 'diagram-events-clipboard-data'
          divElement.innerText = eventsToCopy.map(e => e.description || '').join(', ')

          const htmlData = divElement.outerHTML
          const plainTextData = eventsToCopy.map(e => e.description || '').join(', ')

          try {
            // Always write to navigator.clipboard so it's available for later checks
            await navigator.clipboard.write([
              new ClipboardItem({
                'text/plain': new Blob([plainTextData], { type: 'text/plain' }),
                'text/html': new Blob([htmlData], { type: 'text/html' })
              })
            ])

            // Also set on event.clipboardData if available (for immediate paste)
            if (event && event.clipboardData) {
              event.clipboardData.setData('text/plain', plainTextData)
              event.clipboardData.setData('text/html', htmlData)
              event.preventDefault()
            }

            this.$store.commit('snackbar/showMessage', { content: isCutOperation ? `Cut ${eventsToCopy.length} shapes` : `Copied ${eventsToCopy.length} shapes`, color: 'white' }, { root: true })

            // Clear multi-selection after copy (unless skipped for cut operation)
            if (!skipClearSelection && this.$refs.bpmn) {
              await this.$nextTick()
              this.$refs.bpmn.clearMultiSelection()
            }
          } catch (error) {
            console.error('Failed to copy to clipboard:', error)
            this.$store.commit('snackbar/showMessage', { content: 'Failed to copy', color: 'red' }, { root: true })
            throw error // Re-throw so cut operation knows it failed
          }
        }
      } else if (this.selectedEvent) {
        // Copy single event
        const schemas = this.getSchemasUsedByEvents([this.selectedEvent])
        const jsonData = JSON.stringify({
          ...this.selectedEvent,
          isCutOperation,
          workflowId: this.workflow.id,
          cardTypes: this.cardTypes,
          color: this.selectedEvent.color ? this.selectedEvent.color : this.workflow.eventDefinition.color,
          laneName: this.svgLanesData[this.selectedEvent.laneId].name,
          schemas
        })

        const divElement = document.createElement('div')
        divElement.setAttribute('data-event-json', jsonData)
        divElement.id = 'diagram-event-clipboard-data'
        divElement.innerText = this.selectedEvent?.description || ''

        const htmlData = divElement.outerHTML
        const plainTextData = this.selectedEvent?.description || ''

        try {
          // Always write to navigator.clipboard so it's available for later checks
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/plain': new Blob([plainTextData], { type: 'text/plain' }),
              'text/html': new Blob([htmlData], { type: 'text/html' })
            })
          ])

          // Also set on event.clipboardData if available (for immediate paste)
          if (event && event.clipboardData) {
            event.clipboardData.setData('text/plain', plainTextData)
            event.clipboardData.setData('text/html', htmlData)
            if (document.activeElement.id !== this.selectedEvent.id) {
              event.preventDefault()
            }
          }

          this.$store.commit('snackbar/showMessage', { content: 'Copied', color: 'white' }, { root: true })
        } catch (error) {
          console.error('Failed to copy to clipboard:', error)
          this.$store.commit('snackbar/showMessage', { content: 'Failed to copy', color: 'red' }, { root: true })
        }
      }
    },

    async onPaste (eventOrTarget) {
      // Check if this is a context menu paste (has targetLaneId) or keyboard paste (event)
      let event = null
      let targetLaneId = null
      let targetGroupId = null

      if (eventOrTarget && typeof eventOrTarget === 'object') {
        if (eventOrTarget.targetLaneId !== undefined) {
          // This is from context menu
          targetLaneId = eventOrTarget.targetLaneId
          targetGroupId = eventOrTarget.targetGroupId
        } else {
          // This is a keyboard/clipboard event
          event = eventOrTarget
        }
      }

      const selection = window.getSelection()
      const hasTextSelection = selection && selection.toString().trim().length > 0

      if (event && (document.activeElement !== document.body || hasTextSelection)) {
        // don't catch these events because they can be input fields or user-selected text
        return
      }
      if (this.workflowStatusCompleted) {
        return
      }
      if (!this.$refs.bpmn) {
        return
      }
      // only paste if we don't have a cursor on the event
      // if we have a cursor, then let the browser handle the paste
      if (!event || !this.selectedEvent?.id || (this.selectedEvent.id !== document.activeElement.id)) {
        try {
          let jsonData = null
          let htmlClipboardData = ''

          if (event && event.clipboardData) {
            htmlClipboardData = event.clipboardData.getData('text/html')
          } else {
            const clipboardItems = await navigator.clipboard.read()
            for (const clipboardItem of clipboardItems) {
              if (clipboardItem.types.includes('text/html')) {
                const blob = await clipboardItem.getType('text/html')
                htmlClipboardData = await blob.text()
                break
              }
            }
          }

          if (htmlClipboardData) {
            const parser = new DOMParser()
            const doc = parser.parseFromString(htmlClipboardData, 'text/html')

            // Check for multi-selection clipboard data
            const multiEventsElement = doc.querySelector('[data-events-json]')
            if (multiEventsElement) {
              const customJsonString = multiEventsElement.getAttribute('data-events-json')
              if (customJsonString) {
                jsonData = JSON.parse(customJsonString)
              }
            } else {
              // Check for single event clipboard data
              const customElement = doc.querySelector('[data-event-json]')
              if (customElement) {
                const customJsonString = customElement.getAttribute('data-event-json')
                if (customJsonString) {
                  jsonData = JSON.parse(customJsonString)
                }
              }
            }
          }

          if (jsonData) {
            // Handle multi-selection paste
            if (jsonData.isMultiSelection && Array.isArray(jsonData.events) && jsonData.events.length > 0) {
              const clipboardSchemas = Array.isArray(jsonData.schemas) ? jsonData.schemas : []
              const isCutOperation = !!jsonData.isCutOperation

              // For cut-paste: preserve current behavior (keep IDs when possible, de-dupe by name/type)
              // For copy-paste: only ensure Entity schemas exist; Command/Query schemas will be cloned per card
              const { updatedSchemas, oldToNewSchemaId } = isCutOperation
                ? this.prepareSchemasForPaste(clipboardSchemas)
                : this.prepareSchemasForPaste(clipboardSchemas.filter((s) => s?.type === SchemaType.ENTITY))

              const clipboardSchemaById = new Map(
                clipboardSchemas
                  .filter((s) => s?.id)
                  .map((s) => [s.id, s])
              )

              // We'll build up schemas as we clone per-card (copy-paste only)
              const workingSchemas = updatedSchemas ? [...updatedSchemas] : [...(this.workflow?.schemas || [])]
              let schemasChanged = !!updatedSchemas

              // Sort events in topological order (parents before children)
              const sortedEvents = this.sortEventsByHierarchy(jsonData.events)
              const selectedEventIds = new Set(sortedEvents.map(e => e.id))

              // Validate lanes before pasting
              const lanes = Object.values(this.svgLanesData)
              let effectiveLaneId = targetLaneId // Use context menu lane if provided

              if (!effectiveLaneId) {
                // Fallback to original logic when not pasting from context menu
                if (lanes.length === 0) {
                  // No lanes exist - need to create a new lane
                  const newLane = await this.$refs.bpmn.addLane(
                    this.svgGraphData,
                    this.svgLanesData,
                    this.svgGroupsData,
                    this.workflow,
                    'Lane 1',
                    false,
                    true
                  )
                  effectiveLaneId = newLane.id

                  await this.$nextTick()
                } else {
                  // Check if any laneId from clipboard doesn't exist in current workflow
                  const allLanesValid = sortedEvents.every(event => {
                    return event.laneId && this.svgLanesData[event.laneId]
                  })

                  if (!allLanesValid) {
                    // Some lanes don't exist - use first lane for all shapes
                    effectiveLaneId = lanes[0].id
                  }
                }
              }

              // FIRST PASS: Create all shapes and build complete oldIdToNewId mapping
              const oldIdToNewId = new Map()
              const newIdTooldId = new Map()
              const shapesWithOriginalData = []

              let shapeIndex = 0 // Track which shape we're processing
              for (const eventData of sortedEvents) {
                if (copyPasteHelpers.isValidShape(eventData)) {
                  let shape = copyPasteHelpers.prepareAddShape(
                    eventData,
                    this.selectedEvent,
                    this.svgGraphData,
                    this.svgLanesData,
                    this.workflow,
                    this.cardTypes,
                    true // isMultiShape = true for multi-shape paste
                  )

                  if (shape.cards && shape.cards.length > 0) {
                    const remappedCards = []
                    for (const card of shape.cards) {
                      if (!card?.schemaId) {
                        remappedCards.push(card)
                        continue
                      }

                      // Cut-paste: keep existing mapping behavior
                      if (isCutOperation) {
                        const mappedSchemaId = oldToNewSchemaId.get(card.schemaId)
                        remappedCards.push({ ...card, schemaId: mappedSchemaId || card.schemaId })
                        continue
                      }

                      // Copy-paste: clone non-Entity schemas so each pasted card gets its own schema instance
                      const sourceSchema = clipboardSchemaById.get(card.schemaId)
                      if (!sourceSchema) {
                        remappedCards.push(card)
                        continue
                      }

                      if (sourceSchema.type === SchemaType.ENTITY) {
                        const mappedEntityId = oldToNewSchemaId.get(card.schemaId)
                        remappedCards.push({ ...card, schemaId: mappedEntityId || card.schemaId })
                        continue
                      }

                      const clonedSchema = safeStructuredClone(sourceSchema)
                      const newSchemaId = randomUUID()
                      clonedSchema.id = newSchemaId

                      // Remap entity references inside the cloned schema (if entities were added/mapped)
                      if (clonedSchema.entityId) {
                        clonedSchema.entityId = oldToNewSchemaId.get(clonedSchema.entityId) || clonedSchema.entityId
                      }
                      if (Array.isArray(clonedSchema.fields)) {
                        clonedSchema.fields = clonedSchema.fields.map((field) => {
                          const relatedEntityId = field?.relatedEntityId
                            ? (oldToNewSchemaId.get(field.relatedEntityId) || field.relatedEntityId)
                            : undefined
                          return {
                            ...field,
                            relatedEntityId: relatedEntityId || undefined
                          }
                        })
                      }

                      workingSchemas.push(clonedSchema)
                      schemasChanged = true
                      remappedCards.push({ ...card, schemaId: newSchemaId })
                    }
                    shape.cards = remappedCards
                  }

                  // Only apply context menu lane/group when NO event is selected
                  if (!this.selectedEvent) {
                    // Override laneId for ALL shapes when pasting from context menu
                    if (targetLaneId) {
                      shape = { ...shape, laneId: targetLaneId }
                    } else if (effectiveLaneId) {
                      // Fallback when lanes were invalid or missing
                      shape = { ...shape, laneId: effectiveLaneId }
                    }

                    // Override groupId ONLY for the FIRST shape (input anchor)
                    if (shapeIndex === 0 && targetGroupId !== null && targetGroupId !== undefined) {
                      shape = { ...shape, groupId: targetGroupId }
                    }
                  } else {
                    // When event IS selected, use original fallback logic
                    if (effectiveLaneId) {
                      shape = { ...shape, laneId: effectiveLaneId }
                    }
                  }

                  // Store shape with original event data for second pass
                  shapesWithOriginalData.push({
                    shape,
                    originalEventData: eventData
                  })

                  // Build complete ID mapping
                  oldIdToNewId.set(eventData.id, shape.id)
                  newIdTooldId.set(shape.id, eventData.id)

                  shapeIndex++ // Increment for next shape
                }
              }

              // SECOND PASS: Now that all new IDs are known, set parents correctly
              const shapesToPaste = []

              const adults = []
              let shapesOutput = null
              const selectedEventIdsOld = new Set(shapesWithOriginalData.map(item => item.originalEventData.id))

              // Original events by id, plus parents that already got a pasted child.
              const sourceById = new Map(sortedEvents.map((e) => [e.id, e]))
              const parentsWithAssignedChild = new Set()

              for (const { shape, originalEventData } of shapesWithOriginalData) {
                // Map parents: preserve all parents that are within the selection
                const mappedParents = []

                if (originalEventData.parents && originalEventData.parents.length > 0) {
                  // Process each parent
                  for (const originalParentId of originalEventData.parents) {
                    if (originalParentId === 'start') {
                      mappedParents.push('start')
                    } else if (selectedEventIds.has(originalParentId)) {
                      // Parent is in selection - map to new ID (now available!)
                      const newParentId = oldIdToNewId.get(originalParentId)
                      if (newParentId) {
                        mappedParents.push(newParentId)
                      }
                    } else {
                      mappedParents.push(this.selectedEvent?.id || 'start')
                    }
                  }
                }

                // Set the mapped parents (preserves multiple parents within selection)
                shape.parents = [...new Set(mappedParents)]

                if (shape.conditionLabels && shape.conditionLabels.length > 0) {
                  shape.conditionLabels = shape.conditionLabels.map(label => {
                    if (selectedEventIds.has(label.parentId)) {
                      const newParentId = oldIdToNewId.get(label.parentId)
                      if (newParentId) {
                        return { ...label, parentId: newParentId }
                      }
                    }
                    return label
                  })
                }

                // Determine if this shape is an adult or has external children
                if (!originalEventData.childIds || originalEventData.childIds.length === 0) {
                  adults.push(shape.id)
                } else {
                  // Check if any child is outside the selection
                  const hasExternalChild = originalEventData.childIds.some(childId => !selectedEventIdsOld.has(childId))
                  if (hasExternalChild && !shapesOutput) {
                    shapesOutput = shape.id
                  }
                }

                shapesToPaste.push({
                  shape,
                  shapeOptions: copyPasteHelpers.buildPasteShapeOptions({
                    mainParentId: mappedParents[0],
                    originalEventData,
                    sourceById,
                    selectedEventIds,
                    parentsWithAssignedChild
                  })
                })
              }

              // Batch paste all shapes with single updateWorkflow call
              if (shapesToPaste.length > 0) {
                await this.$refs.bpmn.addMultipleShapes(
                  this.selectedEvent?.id || 'start',
                  shapesToPaste,
                  newIdTooldId,
                  { shapesOutput, adults, schemas: schemasChanged ? workingSchemas : undefined }
                )
                if (event) {
                  event.preventDefault()
                }
                this.$store.commit('snackbar/showMessage', {
                  content: `Pasted ${shapesToPaste.length} events`,
                  color: 'white'
                }, { root: true })
              }
            } else if (copyPasteHelpers.isValidShape(jsonData)) {
              // Handle single event paste (existing logic)
              const clipboardSchemas = Array.isArray(jsonData.schemas) ? jsonData.schemas : []
              const isCutOperation = !!jsonData.isCutOperation

              const { updatedSchemas, oldToNewSchemaId } = isCutOperation
                ? this.prepareSchemasForPaste(clipboardSchemas)
                : this.prepareSchemasForPaste(clipboardSchemas.filter((s) => s?.type === SchemaType.ENTITY))

              const clipboardSchemaById = new Map(
                clipboardSchemas
                  .filter((s) => s?.id)
                  .map((s) => [s.id, s])
              )

              const workingSchemas = updatedSchemas ? [...updatedSchemas] : [...(this.workflow?.schemas || [])]
              let schemasChanged = !!updatedSchemas

              // Validate lanes before pasting
              const lanes = Object.values(this.svgLanesData)
              // Only use context menu lane when NO event is selected (same behavior as existing code)
              let effectiveLaneId = !this.selectedEvent ? targetLaneId : null

              if (!effectiveLaneId) {
                if (lanes.length === 0) {
                  // No lanes exist - need to create a new lane
                  const newLane = await this.$refs.bpmn.addLane(
                    this.svgGraphData,
                    this.svgLanesData,
                    this.svgGroupsData,
                    this.workflow,
                    'Lane 1',
                    false,
                    true
                  )
                  effectiveLaneId = newLane.id

                  await this.$nextTick()
                } else {
                  // If clipboard lane doesn't exist in current workflow, fall back to first lane
                  const laneIsValid = jsonData.laneId && this.svgLanesData[jsonData.laneId]
                  if (!laneIsValid) {
                    effectiveLaneId = lanes[0].id
                  }
                }
              }

              let shape = copyPasteHelpers.prepareAddShape(jsonData, this.selectedEvent, this.svgGraphData, this.svgLanesData, this.workflow, this.cardTypes)

              // Ensure we paste into a valid lane (covers "no lanes" and "invalid clipboard lane" cases)
              if (effectiveLaneId) {
                shape = { ...shape, laneId: effectiveLaneId }
              }

              if (shape.cards && shape.cards.length > 0) {
                const remappedCards = []
                for (const card of shape.cards) {
                  if (!card?.schemaId) {
                    remappedCards.push(card)
                    continue
                  }

                  if (isCutOperation) {
                    const mappedSchemaId = oldToNewSchemaId.get(card.schemaId)
                    remappedCards.push({ ...card, schemaId: mappedSchemaId || card.schemaId })
                    continue
                  }

                  const sourceSchema = clipboardSchemaById.get(card.schemaId)
                  if (!sourceSchema) {
                    remappedCards.push(card)
                    continue
                  }

                  if (sourceSchema.type === SchemaType.ENTITY) {
                    const mappedEntityId = oldToNewSchemaId.get(card.schemaId)
                    remappedCards.push({ ...card, schemaId: mappedEntityId || card.schemaId })
                    continue
                  }

                  const clonedSchema = safeStructuredClone(sourceSchema)
                  const newSchemaId = randomUUID()
                  clonedSchema.id = newSchemaId

                  if (clonedSchema.entityId) {
                    clonedSchema.entityId = oldToNewSchemaId.get(clonedSchema.entityId) || clonedSchema.entityId
                  }
                  if (Array.isArray(clonedSchema.fields)) {
                    clonedSchema.fields = clonedSchema.fields.map((field) => {
                      const relatedEntityId = field?.relatedEntityId
                        ? (oldToNewSchemaId.get(field.relatedEntityId) || field.relatedEntityId)
                        : undefined
                      return {
                        ...field,
                        relatedEntityId: relatedEntityId || undefined
                      }
                    })
                  }

                  workingSchemas.push(clonedSchema)
                  schemasChanged = true
                  remappedCards.push({ ...card, schemaId: newSchemaId })
                }
                shape.cards = remappedCards
              }

              // Override laneId and groupId if pasting from context menu AND no event is selected
              if (!this.selectedEvent) {
                if (targetGroupId !== null && targetGroupId !== undefined) {
                  shape = { ...shape, groupId: targetGroupId }
                }
              }

              await this.$refs.bpmn.addShape(shape, { schemas: schemasChanged ? workingSchemas : undefined })
              if (event) {
                event.preventDefault()
              }
            } else {
              this.$store.commit('snackbar/showMessage', { content: 'Invalid element', color: 'red' }, { root: true })
            }
          }
        } catch (error) {
          // Handle JSON parse errors or any other errors
          console.error('Error parsing clipboard data:', error)
          this.$store.commit('snackbar/showMessage', {
            content: 'Error pasting data. Please try again.',
            color: 'red'
          }, { root: true })
        }
      }
    },

    async deleteMultipleEvents (eventIds) {
      const idsToDelete = eventIds || this.selectedEventIds
      if (!Array.isArray(idsToDelete) || idsToDelete.length === 0) {
        return
      }

      // Clear multi-selection
      if (this.$refs.bpmn) {
        this.$refs.bpmn.clearMultiSelection()
      }
      await this.$nextTick()

      await workflowApi.deleteEventsFromWorkflow(idsToDelete, this.svgGraphData, this.svgLanesData, this.svgGroupsData, this.workflow)
      this.$router.push({
        path: '/workflow/' + this.$route.params.projectId + '/' + this.workflow.id
      }).catch(() => {})
    },

    async onCut (event) {
      const selection = window.getSelection()
      const hasTextSelection = selection && selection.toString().trim().length > 0

      if (event && (document.activeElement !== document.body || hasTextSelection)) {
        // don't catch these events because they can be input fields or user-selected text
        return
      }

      // Check for multi-selection
      const selectedEventIds = this.$refs.bpmn?.selectedEventIds || []
      const hasMultiSelection = Array.isArray(selectedEventIds) && selectedEventIds.length > 1

      if (hasMultiSelection) {
        // Prevent default cut behavior for keyboard shortcuts
        if (event) {
          event.preventDefault()
          event.stopPropagation()
        }

        // Cut multiple events
        // First copy all events - ensure clipboard write completes
        try {
          // Copy without clearing selection (we need it for delete)
          // Pass skipClearSelection = true and isCutOperation = true
          await this.onCopy(event, true, true)

          // Delay to ensure clipboard is fully written and available for checks
          // This gives the browser time to sync the clipboard data
          await this.$nextTick()

          // Then delete all selected events (this will clear selection)
          await this.deleteMultipleEvents(selectedEventIds)
        } catch (error) {
          console.error('Failed to cut events:', error)
          this.$store.commit('snackbar/showMessage', {
            content: 'Failed to cut events. Please try again.',
            color: 'red'
          }, { root: true })
        }
      } else if (!event || (this.selectedEvent?.id && (this.selectedEvent.id !== document.activeElement.id) && this.$refs.deleteItemDialog)) {
        // Cut single event
        if (event) {
          event.preventDefault()
          event.stopPropagation()
        }

        await this.onCopy(event, false, true) // isCutOperation = true
        // Small delay to ensure clipboard is fully written
        await this.$nextTick()
        await this.$refs.deleteItemDialog.deleteItem()
      }
    },
    documentHasDialogOverlay () {
      return document.querySelector('.v-overlay__scrim') !== null
    },
    async saveThumbnail () {
      const svgElement = document.getElementById('svg')?.cloneNode(true)

      if (!svgElement) {
        return
      }

      // Example criteria: Remove elements with a specific class
      // Adjust the selector to match your criteria (e.g., tag name, class, attribute)
      let elementsToRemove = svgElement.querySelectorAll('.swime-lane-path')

      // Iterate over the NodeList and remove each element from the SVG
      elementsToRemove.forEach(element => {
        element.parentNode.removeChild(element)
      })

      elementsToRemove = svgElement.querySelectorAll('.main-rect')
      elementsToRemove.forEach(element => {
        element.parentNode.removeChild(element)
      })

      elementsToRemove = svgElement.querySelectorAll('.add-event-button')
      elementsToRemove.forEach(element => {
        element.parentNode.removeChild(element)
      })

      // Remove rect objects that row is greater than 3 or column is greater 9
      elementsToRemove = svgElement.querySelectorAll('rect')
      elementsToRemove.forEach(element => {
        const xValue = parseFloat(element.getAttribute('x'))
        const yValue = parseFloat(element.getAttribute('y'))
        if (xValue > 1360 || yValue > 423) {
          if (element.parentNode.classList.contains('event-group')) { // event rect
            const toBeRemoved = element.parentNode.parentNode
            toBeRemoved.parentNode.removeChild(toBeRemoved)
          } else if (element.parentNode.parentNode && element.parentNode.parentNode.id === 'svg') { // rect which is connected to the lane
            element.parentNode.parentNode.removeChild(element.parentNode)
          } else { // child rect of svg
            element.parentNode.removeChild(element)
          }
        }
      })

      // Remote foreignObjects that will be invisible in thumbnail
      elementsToRemove = svgElement.querySelectorAll('foreignObject')
      elementsToRemove.forEach(element => {
        const xValue = parseFloat(element.getAttribute('x'))
        const yValue = parseFloat(element.getAttribute('y'))
        if (xValue > 1365 || yValue > 423) {
          element.parentNode.removeChild(element)
        }
      })

      // Remove path and circle that its event row is greater than 3 or column is greater 9
      elementsToRemove = svgElement.querySelectorAll('circle')
      elementsToRemove.forEach(element => {
        const xValue = parseFloat(element.getAttribute('cx'))
        const yValue = parseFloat(element.getAttribute('cy'))
        if (xValue > 1465 || yValue > 390) {
          element.parentNode.removeChild(element.nextSibling) // path
          element.parentNode.removeChild(element) // circle
        }
      })

      elementsToRemove = svgElement.querySelectorAll('mask')
      elementsToRemove.forEach(element => {
        element.parentNode.removeChild(element)
      })

      elementsToRemove = svgElement.querySelectorAll('defs')
      elementsToRemove.forEach(element => {
        if (!element.querySelector('marker')) {
          element.parentNode.removeChild(element)
        }
      })

      svgElement.setAttribute('viewBox', '20 0 3000 450')
      svgElement.setAttribute('height', '146px')
      svgElement.setAttribute('width', '974px')

      const serializer = new XMLSerializer()
      const commentRegex = /<!--[\s\S]*?-->/g
      let svgString = serializer.serializeToString(svgElement)
      let previous
      do {
        previous = svgString
        svgString = svgString.replace(commentRegex, '')
      } while (svgString !== previous)

      const data = await store.dispatch('workflow/updateWorkflowNoHistory', {
        projectId: this.workflow.projectId,
        input: {
          id: this.workflow.id,
          thumbnail: svgString,
          expectedVersion: this.workflow.version
        }
      })

      if (data?.updateWorkflowNoHistory) {
        await store.commit('workflows/updateWorkflows', data?.updateWorkflowNoHistory)
      }
    },

    async updateWorkflowQueries (workflowId) {
      if (workflowId) {
        this.$store.commit('workflow/setLoading', true)

        // Check if viewing a historical version
        if (this.isViewingHistoricalVersion) {
          const version = parseInt(this.$route.params.version)
          const projectId = this.$route.params.projectId

          // First, get the current workflow to check access and get metadata
          await this.$store.dispatch('workflow/getWorkflow', this.$route.params)

          // Load the historical version data
          const historicalWorkflow = await this.$store.dispatch('workflowHistory/getWorkflowHistory', {
            projectId,
            workflowId,
            version
          })

          // Get current workflow metadata for access level and other info
          const currentWorkflow = this.$store.state.workflow.workflow

          // Set the historical workflow as the current workflow (read-only)
          // Ensure all required fields have safe defaults
          this.$store.commit('workflow/setWorkflow', {
            ...historicalWorkflow,
            id: workflowId,
            projectId,
            accessLevel: currentWorkflow?.accessLevel,
            eventDefinition: currentWorkflow?.eventDefinition,
            roleDefinition: currentWorkflow?.roleDefinition,
            boundedContexts: currentWorkflow?.boundedContexts || [],
            groups: currentWorkflow?.groups || [],
            releases: currentWorkflow?.releases || [],
            lanes: historicalWorkflow?.lanes || [],
            domainEvents: (historicalWorkflow?.domainEvents || []).map(event => ({
              ...event,
              cards: (event.cards || []).map(card => ({
                ...card,
                reactions: card.reactions || [],
                comments: card.comments || [],
                attachments: card.attachments || []
              }))
            })),
            schemas: historicalWorkflow?.schemas || []
          })

          // Enable read-only mode
          this.$store.commit('workflow/setViewOnlyMode', true)

          // Still load card types and history for context
          const cardTypesPromise = this.$store.dispatch('cardTypes/listCardTypes', {
            workflowId
          })
          const listWorkflowHistoryPromise = this.$store.dispatch('workflowHistory/listWorkflowHistory', {
            projectId,
            workflowId,
            subscribe: false
          })
          await Promise.all([cardTypesPromise, listWorkflowHistoryPromise])
        } else {
          await this.$store.dispatch('workflow/getWorkflow', this.$route.params)
          syncHelpHeroWorkflow(this.$store.state)
          // run listWorkflowHistory after getWorkflow
          // because we need the current workflow to be set
          // when setting the workflow history in the store
          // also run listCardTypes after getWorkflow
          // so we can avoid subscriptions on public workflows
          const cardTypesPromise = this.$store.dispatch('cardTypes/listCardTypes', {
            workflowId
          })
          const listWorkflowHistoryPromise = this.$store.dispatch('workflowHistory/listWorkflowHistory', {
            projectId: this.$route.params.projectId,
            workflowId
          })
          await Promise.all([cardTypesPromise, listWorkflowHistoryPromise])
        }

        this.$store.commit('workflow/setLoading', false)
      } else {
        this.$store.commit('workflow/setWorkflow', {})
      }
    }
  },
  beforeRouteLeave (to, from, next) {
    if (to.path.includes('/workflow/')) {
      // we are not leaving the workflow, so no need to save the thumbnail
      next()
    } else {
      this.$store.commit('setHasClosedSidePanel', false)
      if (!this.$store.state.isUserLoggedIn) {
        next()
        return
      }
      if (this.$store.state.workflow.isViewOnlyEnabled) {
        // view only mode, cannot save thumbnail
        next()
        return
      }
      this.saveThumbnail().then(() => {
        // save the thumbnail before leaving workflow
        // so that the thumbnail is up to date
        // if we load it again on the next page
        next()
      }).catch((error) => {
        console.error(error)
        next() // if saving the thumbnail fails, we still want to leave the page
      })
    }
  },
  async mounted () {
    EventBus.$on('get-workflow', (workflowId) => {
      this.updateWorkflowQueries(workflowId)
    })

    EventBus.$on('open-tab-event', (params) => {
      const { tabId, eventId } = params

      EventBus.$emit('enable-connection-targets', false)
      this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.$route.params.id + '/' + eventId, query: { selectedTab: tabId } })
    })

    if (!window.Cypress) {
      this.checkVersionTimeout = setTimeout(this.checkForVersionGap, 5000)
    }
  },
  unmounted () {
    clearTimeout(this.checkVersionTimeout)
  }
}
</script>

<style scoped>
.workflow-container {
  transition: margin ease-in-out 0.2s;
}

.admin-warning {
  margin: 0 -40px;
  padding: 6px 0;
  letter-spacing: 0.3px;
}

.cursor-pointer {
  cursor: pointer;
}

.workflow-heading {
  cursor: pointer;
}

.setting-search-field {
  padding-top: 6px;
}

.v-list__tile__sub-title {
  font-size: 10px;
}

.theme--light.v-subheader {
  text-decoration: underline;
  color: var(--text-dark);
}

.container {
  padding-top: 0px;
}

#overlay {
  position: fixed; /* Sit on top of the page content */
  display: none; /* Hidden by default */
  width: 100%; /* Full width (cover the whole page) */
  height: 100%; /* Full height (cover the whole page) */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5); /* Black background with opacity */
  z-index: 2; /* Specify a stack order in case you're using a different order for other elements */
  cursor: pointer; /* Add a pointer on hover */
}

.hidden {
  display: none;
}

.hover-underline:hover {
  text-decoration: underline;
}

.approval-button {
  margin-right: 0px;
}

.v-list__tile_comments > div {
  height: auto;
}

.workflow-settings-button {
  min-width: auto !important;
}

.v-tooltip__content {
  background: var(--light-1);
  color: var(--color-primary);
  border-radius: 10px;
  max-width: 320px;
}

.bottom-container {
  margin: 0 -40px;
  padding: 28px 40px;
  border-radius: 24px 24px 0 0;
  background: var(--light-2);
  min-height: 100vh;
}
</style>

<style>
.tab-button-container {
  width: fit-content;
  height: 38px;
  position: sticky;
  left: 0;
  max-width: calc(100% - 40px);
  overflow: hidden;
  z-index: 1;

  .v-slide-group__content {
    column-gap: 4px;
  }

  .v-tab.v-tab.v-btn {
    min-width: 70px;
  }
}

.tab-button:first-child {
  margin-left: 10px;
}

.tab-button {
  padding: 0 10px;
  max-width: 400px;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.tab-button.active {
  font-weight: 500;
  border-bottom: 2px solid #022a44;
}

.tab-button.inactive {
  color: var(--dark-1);
  font-weight: 400;
}

.tab-button:focus {
  outline: 0;
}

</style>
